import React, { useState } from 'react';
import { Modal, Button, Typography, Steps, Space, App } from 'antd';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const { Text, Paragraph } = Typography;

interface TelegramLinkModalProps {
  visible: boolean;
  onClose: () => void;
  onLinked: () => void;
}

interface LinkCodeResponse {
  code: string;
  expiry: string;
  botUsername: string;
  instructions: {
    pl: string;
    en: string;
  };
}

const TelegramLinkModal: React.FC<TelegramLinkModalProps> = ({ visible, onClose, onLinked }) => {
  const { t } = useTranslation(['notifications', 'common']);
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [linkData, setLinkData] = useState<LinkCodeResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const generateCode = async () => {
    setLoading(true);
    try {
      const response = await api.post<LinkCodeResponse>('/notifications/telegram/link');
      setLinkData(response.data);
    } catch (error) {
      message.error(t('notifications:telegram.linkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (linkData) {
      const command = `/link ${linkData.code}`;
      navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenBot = () => {
    if (linkData?.botUsername) {
      window.open(`https://t.me/${linkData.botUsername}`, '_blank');
    }
  };

  const handleClose = () => {
    setLinkData(null);
    setCopied(false);
    onClose();
  };

  return (
    <Modal
      title={t('notifications:telegram.linkTitle')}
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={500}
    >
      {!linkData ? (
        <Space direction="vertical" style={{ width: '100%', textAlign: 'center', padding: '20px 0' }}>
          <Paragraph>{t('notifications:telegram.linkDescription')}</Paragraph>
          <Button type="primary" onClick={generateCode} loading={loading}>
            {t('notifications:preferences_page.linkTelegram')}
          </Button>
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Steps
            direction="vertical"
            current={-1}
            items={[
              {
                title: t('notifications:telegram.step1'),
                description: (
                  <Space>
                    <Text code>@{linkData.botUsername}</Text>
                    <Button size="small" onClick={handleOpenBot}>
                      {t('common:open')}
                    </Button>
                  </Space>
                ),
              },
              {
                title: t('notifications:telegram.step2'),
                description: (
                  <Space>
                    <Text code>/link {linkData.code}</Text>
                    <Button
                      size="small"
                      icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                      onClick={handleCopy}
                    >
                      {copied ? t('common:copied') : t('common:copy')}
                    </Button>
                  </Space>
                ),
              },
              {
                title: t('notifications:telegram.step3'),
                description: t('notifications:telegram.codeExpires'),
              },
            ]}
          />

          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <Button onClick={handleClose}>{t('common:close')}</Button>
          </div>
        </Space>
      )}
    </Modal>
  );
};

export default TelegramLinkModal;
